import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Save, Trash2, UserPlus, X } from 'lucide-react';
import { Button } from '../../atoms';
import { PageContainer, PageHeader } from '../../templates';
import {
	ConfirmationDialog,
	DetailField,
	DetailSection,
	DetailTextInput,
	InlineNotice,
} from '../../molecules';

export interface CerFormAdmin {
	id: string;
	email: string;
	fullName: string | null;
}

export interface CerFormViewProps {
	nome: string;
	saving: boolean;
	error: string;
	saveDisabled: boolean;
	canManageAdmins: boolean;
	/** Scelta facoltativa del primo amministratore, fornita dall'app. */
	initialAdmin: ReactNode;
	onNameChange: (name: string) => void;
	onSave: () => void;
	onClose: () => void;
}

/**
 * Creazione di una CER: nome e, facoltativo, il primo amministratore. La
 * modifica di una CER esistente sta tutta nel suo dettaglio (INB-374).
 */
export function CerFormView({
	nome,
	saving,
	error,
	saveDisabled,
	canManageAdmins,
	initialAdmin,
	onNameChange,
	onSave,
	onClose,
}: CerFormViewProps) {
	return (
		<PageContainer width="narrow">
			<div className="space-y-5">
				<PageHeader
					size="medium"
					stackOnMobile
					title="Nuova CER"
					actions={
						<>
							<Button type="button" variant="outline" onClick={onClose} disabled={saving}>
								<X />
								Annulla
							</Button>
							<Button
								type="submit"
								form="cer-edit-form"
								variant="accent"
								disabled={saving || saveDisabled}
							>
								<Save />
								{saving ? 'Salvataggio…' : 'Crea CER'}
							</Button>
						</>
					}
				/>
				<form
					id="cer-edit-form"
					className="space-y-4"
					onSubmit={(event) => {
						event.preventDefault();
						onSave();
					}}
				>
					<DetailSection title="Dati CER">
						<DetailField label="Nome / Denominazione">
							<DetailTextInput value={nome} onChange={onNameChange} disabled={saving} />
						</DetailField>
					</DetailSection>
					{canManageAdmins && (
						<DetailSection title="Amministratore (opzionale)">{initialAdmin}</DetailSection>
					)}
					{error && <InlineNotice tone="danger">{error}</InlineNotice>}
				</form>
			</div>
		</PageContainer>
	);
}

export interface CerAdminsSectionProps {
	admins: CerFormAdmin[];
	/** Modulo per aggiungerne uno, fornito dall'app (cerca l'utente per email). */
	addAdmin: ReactNode;
	pendingRemove: CerFormAdmin | null;
	onRemove: (admin: CerFormAdmin) => void;
	onConfirmRemove: () => void;
	onCancelRemove: () => void;
}

/**
 * Gli amministratori di una CER, con aggiunta e rimozione. Sta nel dettaglio
 * della CER: prima vivevano in una pagina a parte, aperta dalla matita, e la
 * stessa CER si modificava da due ingressi diversi (INB-374).
 */
export function CerAdminsSection({
	admins,
	addAdmin,
	pendingRemove,
	onRemove,
	onConfirmRemove,
	onCancelRemove,
}: CerAdminsSectionProps) {
	const [addingAdmin, setAddingAdmin] = useState(false);
	// Aggiunto un amministratore, il modulo si chiude: restava aperto e vuoto,
	// come se l'aggiunta non fosse andata.
	const adminCount = useRef(admins.length);
	useEffect(() => {
		if (admins.length > adminCount.current) setAddingAdmin(false);
		adminCount.current = admins.length;
	}, [admins.length]);
	return (
		<DetailSection title="Amministratori">
			{admins.length === 0 ? (
				<p className="text-sm text-text-muted">Nessun amministratore assegnato.</p>
			) : (
				<ul className="space-y-3">
					{admins.map((admin) => (
						<li key={admin.id} className="flex items-center justify-between gap-3">
							<div className="min-w-0">
								<p className="truncate text-sm font-semibold">{admin.fullName || admin.email}</p>
								{admin.fullName && (
									<p className="truncate text-xs text-text-muted">{admin.email}</p>
								)}
							</div>
							<Button
								type="button"
								variant="ghostDanger"
								size="icon"
								aria-label={`Rimuovi ${admin.fullName || admin.email}`}
								onClick={() => onRemove(admin)}
							>
								<Trash2 />
							</Button>
						</li>
					))}
				</ul>
			)}
			<Button
				type="button"
				variant="accentSoft"
				size="sm"
				onClick={() => setAddingAdmin((open) => !open)}
			>
				<UserPlus />
				Aggiungi amministratore
			</Button>
			{addingAdmin && addAdmin}
			<ConfirmationDialog
				open={!!pendingRemove}
				onOpenChange={(open) => {
					if (!open) onCancelRemove();
				}}
				title="Rimuovere l'amministratore?"
				description={`${pendingRemove?.fullName || pendingRemove?.email} non amministrerà più questa CER.`}
				confirmText="Sì, rimuovi"
				cancelText="Annulla"
				onConfirm={onConfirmRemove}
				variant="destructive"
			/>
		</DetailSection>
	);
}
